import type { DiagramProps } from '@/lib/scad-feature-types';
import { D } from './DiagramStyles';

/** Sweep arc path helper */
function arcPath(cx: number, cy: number, rx: number, ry: number, startDeg: number, endDeg: number): string {
  const s = (startDeg * Math.PI) / 180;
  const e = (endDeg * Math.PI) / 180;
  const x1 = cx + rx * Math.cos(s);
  const y1 = cy + ry * Math.sin(s);
  const x2 = cx + rx * Math.cos(e);
  const y2 = cy + ry * Math.sin(e);
  const large = Math.abs(endDeg - startDeg) > 180 ? 1 : 0;
  return `M${x1},${y1} A${rx},${ry} 0 ${large},1 ${x2},${y2}`;
}

export function RotateDiagram({ values }: DiagramProps) {
  const a = values.a ?? [90, 0, 0];
  const ax = a[0] ?? 0;
  const ay = a[1] ?? 0;
  const az = a[2] ?? 0;

  // Show a rectangular block and three rotation arc indicators
  const bx = 112, by = 72; // block center
  const bW = 28, bH = 20;

  // Clamp display arc to sensible range
  const dispX = Math.min(Math.abs(ax), 270) * Math.sign(ax || 1);
  const dispY = Math.min(Math.abs(ay), 270) * Math.sign(ay || 1);
  const dispZ = Math.min(Math.abs(az), 270) * Math.sign(az || 1);

  return (
    <svg viewBox="0 0 280 150" width="100%" style={{ display: 'block' }} aria-label="Rotate diagram">
      <defs>
        <marker id="arc-x" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M0,1 L5,3 L0,5 Z" fill={D.X_COLOR} />
        </marker>
        <marker id="arc-y" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M0,1 L5,3 L0,5 Z" fill={D.Y_COLOR} />
        </marker>
        <marker id="arc-z" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
          <path d="M0,1 L5,3 L0,5 Z" fill={D.Z_COLOR} />
        </marker>
      </defs>

      {/* Central block */}
      <rect x={bx - bW / 2} y={by - bH / 2} width={bW} height={bH}
            fill={D.GEO_FILL} stroke={D.GEO_STROKE} strokeWidth="1.5" rx="2" />

      {/* X axis arc (red) — rotates around X axis, shown as vertical arc to the left */}
      {ax !== 0 && (
        <>
          <path d={arcPath(bx - 50, by, 18, 28, -60, -60 + dispX)}
                fill="none" stroke={D.X_COLOR} strokeWidth="1.8" opacity="0.85"
                markerEnd="url(#arc-x)" />
          <line x1={bx - 50} y1={by - 30} x2={bx - 50} y2={by + 30}
                stroke={D.X_COLOR} strokeWidth="1" strokeDasharray="3 3" opacity="0.5" />
          <text x={bx - 50} y={by + 42} fontSize="10" fontFamily="monospace"
                fill={D.X_COLOR} textAnchor="middle" opacity="0.9">X {ax}°</text>
        </>
      )}

      {/* Y axis arc (green) — rotates around Y, shown as horizontal arc below */}
      {ay !== 0 && (
        <>
          <path d={arcPath(bx, by + 42, 28, 12, 180, 180 + dispY)}
                fill="none" stroke={D.Y_COLOR} strokeWidth="1.8" opacity="0.85"
                markerEnd="url(#arc-y)" />
          <line x1={bx - 30} y1={by + 42} x2={bx + 30} y2={by + 42}
                stroke={D.Y_COLOR} strokeWidth="1" strokeDasharray="3 3" opacity="0.5" />
          <text x={bx} y={by + 62} fontSize="10" fontFamily="monospace"
                fill={D.Y_COLOR} textAnchor="middle" opacity="0.9">Y {ay}°</text>
        </>
      )}

      {/* Z axis arc (blue) — rotates around Z, shown as circular arc around the box */}
      {az !== 0 && (
        <>
          <path d={arcPath(bx + 52, by, 18, 28, 120, 120 + dispZ)}
                fill="none" stroke={D.Z_COLOR} strokeWidth="1.8" opacity="0.85"
                markerEnd="url(#arc-z)" />
          <line x1={bx + 52} y1={by - 30} x2={bx + 52} y2={by + 30}
                stroke={D.Z_COLOR} strokeWidth="1" strokeDasharray="3 3" opacity="0.5" />
          <text x={bx + 52} y={by + 42} fontSize="10" fontFamily="monospace"
                fill={D.Z_COLOR} textAnchor="middle" opacity="0.9">Z {az}°</text>
        </>
      )}

      {/* Fallback when all zeros */}
      {ax === 0 && ay === 0 && az === 0 && (
        <text x={bx} y={by + 38} fontSize="9" fontFamily="monospace"
              fill="currentColor" opacity="0.45" textAnchor="middle">set angle to see rotation</text>
      )}

      {/* Angle values */}
      <text x="8" y="142" fontSize="9" fontFamily="monospace"
            fill="currentColor" opacity="0.45">a = [X°, Y°, Z°]</text>
    </svg>
  );
}
